import { generateUuid } from '../utils/bip32';
import { hashStringAsync } from '../utils/canonical';
import { signMessage } from '../utils/crypto';
import { encryptWithECDH } from '../utils/encryption';
import type { LocalIdentity } from '../types/identity';
import type { LoginChallenge, LoginProof } from '../types/identity';
import type { MsgChiffre, MsgCle } from '../types/message';

const ECDH_ALGO = 'AES-GCM-ECDH';

/**
 * Service for building and publishing login messages.
 * Login uses ECDH (identity as recipient) and MsgCle with df_ecdh_scannable for scan → fetch → decrypt.
 */
export class LoginBuilder {
  private readonly identity: LocalIdentity;
  private readonly relays: string[];

  constructor(identity: LocalIdentity, relays: string[]) {
    this.identity = identity;
    this.relays = relays;
  }

  /**
   * Build a login challenge message.
   * Encrypts with ECDH to identity public key; iv and df_ecdh_scannable stored for MsgCle.
   */
  async buildChallenge(
    serviceUuid: string,
    typeUuid: string,
    _actionLoginUuid: string,
    _membreUuid: string,
  ): Promise<LoginChallenge> {
    const pk = this.identity.privateKey;
    if (pk === undefined) {
      throw new Error('Private key required to build login challenge (ECDH)');
    }
    const nonce = generateUuid();
    const timestamp = Date.now();

    const datajsonPublic = {
      services_uuid: [serviceUuid],
      types_uuid: [typeUuid],
      timestamp,
    };

    const messageBase = {
      uuid: generateUuid(),
      version: '1.0',
      types: {
        types_uuid: [typeUuid],
        types_names_chiffres: 'login',
      },
      datajson: datajsonPublic,
      timestamp,
      liste_relais: this.relays,
      version_logicielle: '1.0.0',
    };

    const messageJson = JSON.stringify(messageBase);
    const hash = await hashStringAsync(messageJson, 'sha256');

    const { encrypted, iv, publicKey } = await encryptWithECDH(
      messageJson,
      this.identity.publicKey,
      pk,
    );

    return {
      hash,
      message_chiffre: encrypted,
      datajson_public: datajsonPublic,
      nonce,
      timestamp,
      iv,
      df_ecdh_scannable: publicKey,
    };
  }

  /**
   * Sign a challenge with a pair's private key.
   * Signs hash-nonce only (verification and relay fetch use same format; pair_uuid is metadata).
   */
  signChallenge(
    challenge: LoginChallenge,
    _pairUuid: string,
    pairPrivateKey: string,
  ): string {
    const messageToSign = `${challenge.hash}-${challenge.nonce}`;
    return signMessage(messageToSign, pairPrivateKey);
  }

  /**
   * Build login proof with signatures.
   */
  async buildProof(
    challenge: LoginChallenge,
    signatures: Array<{
      signature: string;
      cle_publique: string;
      nonce: string;
      pair_uuid: string;
    }>,
  ): Promise<LoginProof> {
    return {
      challenge,
      signatures,
      statut: 'en_attente',
    };
  }

  /**
   * Convert challenge to MsgChiffre for publication.
   */
  challengeToMsgChiffre(challenge: LoginChallenge): MsgChiffre {
    return {
      hash: challenge.hash,
      message_chiffre: challenge.message_chiffre,
      datajson_public: challenge.datajson_public,
    };
  }

  /**
   * Build MsgCle for login challenge (ECDH).
   * Use when publishing login to relays for scan → fetch → decrypt flow.
   */
  challengeToMsgCle(challenge: LoginChallenge): MsgCle | null {
    const iv = challenge.iv;
    const df = challenge.df_ecdh_scannable;
    if (iv === undefined || df === undefined) {
      return null;
    }
    return {
      hash_message: challenge.hash,
      cle_de_chiffrement_message: {
        algo: ECDH_ALGO,
        params: { iv },
      },
      df_ecdh_scannable: df,
    };
  }
}
